import { AsyncDirective, directive } from "lit/async-directive.js";
import { type ElementPart, type PartInfo, PartType } from "lit/directive.js";
import tippy, { type Instance, type Props } from "tippy.js";
import tippyCSS from "tippy.js/dist/tippy.css?inline";

// Tippy's stylesheet lives in the light DOM (tooltips render to document.body
// so shadow-scoped styles can't reach them). Injected once on module load so
// library consumers don't have to remember a separate CSS import. Also
// appends overrides that force the editor's system font stack + a tighter
// look — tippy's stock stylesheet inherits the browser default (Times New
// Roman in most contexts), which reads terrible next to a native-feeling UI.
const STYLE_ID = "__dxf_editor_tippy_css__";
const TIPPY_OVERRIDES = `
.tippy-box[data-theme~="dxf-editor"],
.tippy-box:not([data-theme]) {
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
    "Helvetica Neue", Arial, sans-serif;
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.01em;
  background-color: #1f1f1f;
  color: #f0f0f0;
  border-radius: 5px;
  padding: 1px 2px;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.32),
              0 1px 2px rgba(0, 0, 0, 0.24);
}
.tippy-box:not([data-theme]) > .tippy-arrow {
  color: #1f1f1f;
}
.tippy-box:not([data-theme]) .tippy-content {
  padding: 5px 9px;
}
`;
if (typeof document !== "undefined" && !document.getElementById(STYLE_ID)) {
  const s = document.createElement("style");
  s.id = STYLE_ID;
  s.textContent = tippyCSS + TIPPY_OVERRIDES;
  document.head.appendChild(s);
}

// Sensible defaults for every tooltip in the editor. Show delay is short so
// hovers feel instant (a hair of debounce prevents flickers on quick pointer
// travel), the show/hide animations are trimmed to ~60ms so the whole thing
// snaps in and out, and the tooltip is appended to body so it escapes any
// shadow-DOM container and never gets clipped by overflow.
const DEFAULTS: Partial<Props> = {
  placement: "bottom",
  delay: [60, 0],
  arrow: true,
  appendTo: () => document.body,
  animation: "fade",
  offset: [0, 6],
  duration: [60, 40],
  hideOnClick: true,
};

class TooltipDirective extends AsyncDirective {
  // Explicit `| undefined` (rather than `?:`) so `= undefined` reassignment
  // clears cleanly under `exactOptionalPropertyTypes`.
  private instance: Instance | undefined = undefined;
  private lastElement: Element | undefined = undefined;

  constructor(partInfo: PartInfo) {
    super(partInfo);
    if (partInfo.type !== PartType.ELEMENT) {
      throw new Error(
        "tooltip() directive can only be attached to an element (use ${tooltip(...)} inside a tag)"
      );
    }
  }

  // The render signature is what shows up at the call site. Lit invokes
  // `update()` when the element is mounted / re-committed; keep render as a
  // no-op so the type just captures the args.
  render(_content: string, _opts?: Partial<Props>): unknown {
    return undefined;
  }

  override update(part: ElementPart, [content, opts]: [string, Partial<Props>?]) {
    const el = part.element;
    const hasContent = typeof content === "string" && content.length > 0;

    if (!hasContent) {
      // Empty content is the caller's way of saying "no tooltip right now"
      // (e.g. an inline-rename input that should suppress its hover hint
      // while editing). Tear down any prior instance so the DOM stays clean;
      // if content becomes non-empty later we re-create fresh.
      this.instance?.destroy();
      this.instance = undefined;
      this.lastElement = el;
      return this.render(content, opts);
    }

    if (this.lastElement !== el || !this.instance) {
      // Element changed OR we skipped creation on a previous empty-content
      // pass: build a fresh instance against the current node.
      this.instance?.destroy();
      this.lastElement = el;
      this.instance = tippy(el as Element, {
        ...DEFAULTS,
        ...(opts ?? {}),
        content,
      });
    } else {
      // Same element, template just re-rendered: update in place so dynamic
      // labels ("Healing…") swap without recreating the tooltip.
      this.instance.setContent(content);
      if (opts) this.instance.setProps({ ...DEFAULTS, ...opts, content });
    }
    return this.render(content, opts);
  }

  override disconnected(): void {
    this.instance?.destroy();
    this.instance = undefined;
    this.lastElement = undefined;
  }
}

// Usage:  html`<button ${tooltip("Open DXF")}>...</button>`
// Or with options:  html`<button ${tooltip("Fit", { placement: "top" })}>`
export const tooltip = directive(TooltipDirective);
